// Type, as a component (kit-sheet "Type roles").
//
// The three faces are role tokens and a call site names a *role*, never a
// family — which is the whole point of the tokens being placeholders: the
// designer swaps a face in one theme line and every `Text` moves with it.
//
// **`tone` is a small closed set, not any colour.** Five names cover every
// piece of prose this interface has; a sixth is a token the sheet is missing,
// not a `sx={{ color }}` at the call site.

import { styled } from "@mui/material/styles";

export type TextRole = "heading" | "body" | "display";
export type TextTone = "ink" | "dim" | "brand" | "cream" | "rust";

export interface TextOwnProps {
  role?: TextRole;
  tone?: TextTone;
  /** px. The scale is small enough that a number reads better than a name. */
  size?: number;
  weight?: 400 | 500 | 700;
  block?: boolean;
}

const OWN = new Set(["role", "tone", "size", "weight", "block"]);

export const Text = styled("span", {
  shouldForwardProp: (prop: string) => !OWN.has(prop)
})<TextOwnProps>(({ theme, role = "body", tone = "ink", size = 14, weight, block }) => {
  const c = theme.kit.color;
  const tones = {
    ink: c.ink,
    dim: c.inkDim,
    brand: c.brand,
    cream: c.cream,
    rust: c.rust
  };
  return {
    fontFamily: theme.kit.font[role],
    fontSize: size,
    color: tones[tone],
    ...(weight ? { fontWeight: weight } : {}),
    ...(block ? { display: "block" } : {}),
    // The display role carries the numerals, so a column of counts lines up
    // without anybody laying out a table.
    ...(role === "display" ? { fontVariantNumeric: "tabular-nums" as const } : {})
  };
});

/** A link in prose. Brand, and underlined only on the way past. */
export const Link = styled("a")(({ theme }) => ({
  color: theme.kit.color.brand,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
  "&:focus-visible": {
    outline: `2px solid ${theme.kit.color.brand}`,
    outlineOffset: 2
  }
}));
